import icons from '@/assets/icons';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import '../../../../dashboard-reset.css';

interface LlmDoctorsNoteCalloutProps {
  onGenerateClick?: () => void;
}

export const LlmDoctorsNoteCallout = ({ onGenerateClick }: LlmDoctorsNoteCalloutProps) => {
  const navigate = useNavigate();
  const { callLinkClicked } = useAnalyticsCalls();

  const handleGenerateClick = () => {
    callLinkClicked('Generate Doctor Letter - LLM', '/doctors-note/generate');

    if (onGenerateClick) {
      onGenerateClick();
    } else {
      // TODO: Navigate to letter generation route when ready
      // navigate(ROUTE_DOCTORS_NOTE_GENERATE);
      console.log('Generate letter clicked');
    }
  };

  return (
    <div className="dashboard-reset w-full">
      {/* Outer wrapper with hero background */}
      <div className="relative overflow-hidden rounded-2xl bg-[#183344]">
        {/* Background Video - twilight sky with clouds */}
        <video
          src="/videos/twilight.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute left-0 top-0 z-0 h-full w-full object-cover"
        />

        {/* Dark overlay for better text contrast */}
        <div className="absolute left-0 top-0 z-[1] h-full w-full bg-black/70" />

        {/* Content card with frosted glass effect */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 px-6 py-6"
          style={{
            backdropFilter: 'blur(24px) saturate(120%)',
            WebkitBackdropFilter: 'blur(24px) saturate(120%)',
          }}
        >
          {/* Icon + Header + Badge Row */}
          <div className="mb-4 flex items-center gap-3">
            {/* Document Icon */}
            <img
              src={icons['Clinical-Dr']}
              alt=""
              className="h-8 w-8 opacity-80 brightness-0 invert"
              aria-hidden="true"
            />

            {/* Header */}
            <h3 className="text-xl font-bold text-white/90">Letter to Doctor</h3>

            {/* NEW Badge */}
            <span className="rounded px-2 py-1 text-xs font-bold uppercase" style={{ backgroundColor: '#5db89a', color: '#183344' }}>
              New
            </span>
          </div>

          {/* Description */}
          <p className="mb-5 text-sm leading-relaxed text-gray-400">
            Create a personalized letter explaining your elevated cancer risks and recommended screenings. Share it with
            your doctor to start a conversation about early detection.
          </p>

          {/* CTA Button with Rainbow Border */}
          <div className="relative rounded-full p-[2px]">
            {/* Animated rainbow gradient background for button border */}
            <div
              className="absolute inset-0 rounded-full opacity-80"
              style={{
                background: `linear-gradient(
                  90deg,
                  #ff0080 0%,
                  #ff4d94 12.5%,
                  #ff8c00 25%,
                  #ffd700 37.5%,
                  #00d4ff 50%,
                  #4da6ff 62.5%,
                  #7b68ee 75%,
                  #b857ff 87.5%,
                  #ff0080 100%
                )`,
                backgroundSize: '300% 100%',
                animation: 'rainbow-shift 6s linear infinite',
              }}
            />

            {/* Button content */}
            <button
              onClick={handleGenerateClick}
              className="relative z-10 w-full rounded-full bg-catch-dark-blue-90 px-6 py-3 text-base font-bold text-white transition-opacity hover:opacity-90"
              aria-label="Generate an AI-powered letter to share with your doctor"
            >
              Generate Letter
            </button>
          </div>
        </motion.div>
      </div>

      {/* Keyframe animation styles */}
      <style>{`
        @keyframes rainbow-shift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        /* Respect user's motion preferences */
        @media (prefers-reduced-motion: reduce) {
          [style*="rainbow-shift"] {
            animation-duration: 10s !important;
          }
        }
      `}</style>
    </div>
  );
};
